import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from './hooks/useQueryClient'
import { useAppStore } from './stores/useAppStore'
import { offlineStorage } from './services/offlineStorage'
import { useSyncStatus } from './hooks/useSyncStatus'
import './serviceWorker'

// Import databaseManager to ensure it's loaded and attached to window in development
import './services/databaseManager'

// Components
import Navigation from './components/Navigation'
import LoginForm from './components/LoginForm'
import ProtectedRoute from './components/ProtectedRoute'
import TechnicianWorkOrder from './components/TechnicianWorkOrder'
import NotificationPermissionPrompt from './components/NotificationPermissionPrompt'

import JSATravel from './components/JSATravel'

// Pages
import WorkOrdersList from './pages/WorkOrdersList'
import Schedule from './pages/Schedule'
import Reports from './pages/Reports'
import Runsheet from './pages/Runsheet'
import Messages from './pages/Messages'
import SyncManagement from './pages/SyncManagement'
import PaymentSuccess from './pages/PaymentSuccess'
import XeroPaymentSuccess from './pages/XeroPaymentSuccess'

// Debug component to log authentication state
const AuthDebugger = () => {
  const { user, token, tokenExpires, isAuthenticated, isTokenExpired } = useAppStore()
  
  useEffect(() => {
  }, [user, token, tokenExpires, isAuthenticated, isTokenExpired])
  
  return null
}

// Admin Route wrapper
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated } = useAppStore()
  
  // If not authenticated, let ProtectedRoute handle it
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />
  }
  
  const isAdmin = user?.roles?.includes('Admin') || user?.roles?.includes('Administrator')
  return isAdmin ? <>{children}</> : <Navigate to="/login" replace />
}

// Technician Route wrapper  
const TechnicianRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated } = useAppStore()
  
  // If not authenticated, let ProtectedRoute handle it
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />
  }
  
  const isTechnician = user?.roles?.includes('Technician')
  const isAdmin = user?.roles?.includes('Admin') || user?.roles?.includes('Administrator')
  
  if (isTechnician) {
    return <>{children}</>
  } else if (isAdmin) {
    return <Navigate to="/admin" replace />
  } else {
    // User has no valid roles, logout and redirect to login
    return <Navigate to="/login" replace />
  }
}

// Login Route wrapper
const LoginRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAppStore()
  
  
  if (isAuthenticated()) {
    return <Navigate to="/" replace />
  }
  
  return <>{children}</>
}

// Admin Dashboard placeholder
const AdminDashboard = () => {
  return (
    <div className="container-mobile py-6">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
          <div className="text-4xl mb-4">⚙️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Administrator Dashboard</h2>
          <p className="text-gray-600 mb-6">Manage system settings and user accounts</p>
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Coming Soon</h3>
            <p className="text-blue-700">Administrative features are currently under development.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Notification Center Component
const NotificationCenter = () => {
  const { notifications, removeNotification } = useAppStore()

  // Auto-remove notifications after timeout
  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = []

    notifications.forEach((notification) => {
      // Different timeouts based on notification type
      const timeout = 1000 // Quick 1 second display
      
      const timeoutId = setTimeout(() => {
        removeNotification(notification.id)
      }, timeout)

      timeouts.push(timeoutId)
    })

    // Cleanup timeouts
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout))
    }
  }, [notifications, removeNotification])

  if (notifications.length === 0) return null

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {notifications.map((notification) => (
        <div 
          key={notification.id}
          className={`
            animate-in slide-in-from-right-full duration-300 ease-out
            bg-white rounded-lg shadow-lg border-l-4 p-4 transition-all
            hover:shadow-xl hover:scale-[1.02]
            ${notification.type === 'success' ? 'border-green-500' : ''}
            ${notification.type === 'error' ? 'border-red-500' : ''}
            ${notification.type === 'warning' ? 'border-yellow-500' : ''}
            ${notification.type === 'info' ? 'border-blue-500' : ''}
          `}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-2">
              <span className={`
                text-sm font-medium
                ${notification.type === 'success' ? 'text-green-800' : ''}
                ${notification.type === 'error' ? 'text-red-800' : ''}
                ${notification.type === 'warning' ? 'text-yellow-800' : ''}
                ${notification.type === 'info' ? 'text-blue-800' : ''}
              `}>
                {notification.message}
              </span>
            </div>
            <button 
              onClick={() => removeNotification(notification.id)}
              className="text-gray-400 hover:text-gray-600 touch-target transition-all hover:scale-110"
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
          
          {/* Progress bar for timeout visualization */}
          <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`
                h-full transition-all ease-linear rounded-full
                ${notification.type === 'success' ? 'bg-green-500' : ''}
                ${notification.type === 'error' ? 'bg-red-500' : ''}
                ${notification.type === 'warning' ? 'bg-yellow-500' : ''}
                ${notification.type === 'info' ? 'bg-blue-500' : ''}
              `}
              style={{
                animation: `shrink 1s linear forwards`
              }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

// Main App Layout
const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const { setOnlineStatus, addNotification } = useAppStore()
  
  // Initialize sync status monitoring
  const { fetchSyncStatus } = useSyncStatus()

  useEffect(() => {
    // Initialize offline storage and notifications
    offlineStorage.init()
    
    // Initialize sync status monitoring
    fetchSyncStatus()
    
    // Initialize notifications
    import('./services/notificationService').then(({ notificationService }) => {
      notificationService.init()
    })
    
    // Setup online/offline event listeners
    const handleOnline = async () => {
      setOnlineStatus(true)
      addNotification({
        type: 'success',
        message: 'Connection restored! Syncing data...',
        timestamp: Date.now()
      })
      
      // Immediate sync on reconnection with notifications
      const { offlineWorkOrderService } = await import('./services/offlineWorkOrderService')
      const { notificationService } = await import('./services/notificationService')
      
      try {
        const hadPending = await offlineWorkOrderService.hasPendingChanges()
        await offlineWorkOrderService.syncAllPendingChanges()
        
        // Notify sync completion
        if (hadPending) {
          await notificationService.notifySyncComplete(true)
          addNotification({
            type: 'success',
            message: 'Offline changes synced successfully!',
            timestamp: Date.now()
          })
        }
      } catch {
        await notificationService.notifySyncError()
      }
    }

    const handleOffline = () => {
      setOnlineStatus(false)
      addNotification({
        type: 'warning',
        message: 'You are now offline. Data will sync when connection returns.',
        timestamp: Date.now()
      })
    }

    // Periodic sync every 2 minutes when online (main thread)
    const syncInterval = setInterval(async () => {
      if (navigator.onLine) {
        const { offlineWorkOrderService } = await import('./services/offlineWorkOrderService')
        await offlineWorkOrderService.syncAllPendingChanges()
      }
    }, 30 * 1000)

    // Service worker message listener for background sync
    const handleSWMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'SYNC_PENDING_CHANGES') {
        const { offlineWorkOrderService } = await import('./services/offlineWorkOrderService')
        const { notificationService } = await import('./services/notificationService')
        
        try {
          const hadPending = await offlineWorkOrderService.hasPendingChanges()
          await offlineWorkOrderService.syncAllPendingChanges()
          
          // Send notification if sync completed successfully
          if (hadPending) {
            await notificationService.notifySyncComplete(true)
          }
        } catch {
          await notificationService.notifySyncError()
        }
      }
    }

    // Register service worker background sync
    navigator.serviceWorker?.ready.then(registration => {
      if ('sync' in registration) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (registration as any).sync.register('pending-sync')
      }
    })

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    navigator.serviceWorker?.addEventListener('message', handleSWMessage)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage)
      clearInterval(syncInterval)
    }
  }, [setOnlineStatus, addNotification, fetchSyncStatus])

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col safe-area">
      <Navigation />
      <main className="flex-1 pb-4">
        {children}
      </main>
      <NotificationCenter />
      {/* <NotificationPermissionPrompt /> */}
    </div>
  )
}

function App() {
  const { isAuthenticated, user, token } = useAppStore()
  
  useEffect(() => {
  }, [isAuthenticated, user, token])

  return (
    <QueryClientProvider client={queryClient}>
      <AuthDebugger />
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              <LoginRoute>
                <div className="app">
                  <LoginForm />
                  <NotificationCenter />
                </div>
              </LoginRoute>
            } 
          />

          {/* Payment Success Routes - Public (no auth required) */}
          <Route 
            path="/payment-success" 
            element={
              <div className="app">
                <PaymentSuccess />
                <NotificationCenter />
              </div>
            } 
          />

          <Route 
            path="/xero-payment-success" 
            element={
              <div className="app">
                <XeroPaymentSuccess />
                <NotificationCenter />
              </div>
            } 
          />

          {/* Debug route to clear authentication */}
          <Route 
            path="/debug/clear-auth" 
            element={
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <h2>Debug: Authentication State</h2>
                <DebugAuthState />
                <div style={{ marginTop: '20px' }}>
                  <button 
                    onClick={() => {
                      localStorage.removeItem('app-storage')
                      window.location.href = '/login'
                    }}
                    style={{ padding: '10px 20px', fontSize: '16px', marginRight: '10px' }}
                  >
                    Clear Auth Data & Go to Login
                  </button>
                  <button 
                    onClick={() => {
                      window.location.href = '/'
                    }}
                    style={{ padding: '10px 20px', fontSize: '16px' }}
                  >
                    Go to Home
                  </button>
                </div>
              </div>
            } 
          />

          {/* Protected Routes - using exact paths to avoid conflict with /login */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <AppLayout>
                  <TechnicianRoute>
                    <WorkOrdersList />
                  </TechnicianRoute>
                </AppLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/work-orders/*" 
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Routes>
                    <Route 
                      index
                      element={
                        <TechnicianRoute>
                          <WorkOrdersList />
                        </TechnicianRoute>
                      } 
                    />
                    <Route 
                      path=":workOrderId" 
                      element={
                        <TechnicianRoute>
                          <TechnicianWorkOrderRoute />
                        </TechnicianRoute>
                      } 
                    />
                  </Routes>
                </AppLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/schedule" 
            element={
              <ProtectedRoute>
                <AppLayout>
                  <TechnicianRoute>
                    <Schedule />
                  </TechnicianRoute>
                </AppLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/reports" 
            element={
              <ProtectedRoute>
                <AppLayout>
                  <TechnicianRoute>
                    <Reports />
                  </TechnicianRoute>
                </AppLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/jsa-travel" 
            element={
              <ProtectedRoute>
                <AppLayout>
                  <TechnicianRoute>
                    <JSATravel />
                  </TechnicianRoute>
                </AppLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/runsheet" 
            element={
              <ProtectedRoute>
                <AppLayout>
                  <TechnicianRoute>
                    <Runsheet />
                  </TechnicianRoute>
                </AppLayout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/messages" 
            element={
              <ProtectedRoute>
                <AppLayout>
                  <TechnicianRoute>
                    <Messages />
                  </TechnicianRoute>
                </AppLayout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/sync" 
            element={
              <ProtectedRoute>
                <AppLayout>
                  <TechnicianRoute>
                    <SyncManagement />
                  </TechnicianRoute>
                </AppLayout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/*" 
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Routes>
                    <Route 
                      index
                      element={
                        <AdminRoute>
                          <AdminDashboard />
                        </AdminRoute>
                      } 
                    />
                    <Route 
                      path="users" 
                      element={
                        <AdminRoute>
                          <div className="page-container">
                            <div className="coming-soon">
                              <h3>User Management Coming Soon</h3>
                            </div>
                          </div>
                        </AdminRoute>
                      } 
                    />
                    <Route 
                      path="settings" 
                      element={
                        <AdminRoute>
                          <div className="page-container">
                            <div className="coming-soon">
                              <h3>Settings Coming Soon</h3>
                            </div>
                          </div>
                        </AdminRoute>
                      } 
                    />
                  </Routes>
                </AppLayout>
              </ProtectedRoute>
            } 
          />

          {/* 404 Route */}
          <Route 
            path="*" 
            element={
              <ProtectedRoute>
                <AppLayout>
                  <div className="page-container">
                    <div className="error-container">
                      <h3>Page Not Found</h3>
                      <p>The page you're looking for doesn't exist.</p>
                    </div>
                  </div>
                </AppLayout>
              </ProtectedRoute>
            } 
          />
        </Routes>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

// Work Order Route component to handle parameters
const TechnicianWorkOrderRoute = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>()
  
  if (!workOrderId || isNaN(Number(workOrderId))) {
    return (
      <div className="page-container">
        <div className="error-container">
          <h3>Invalid Work Order</h3>
          <p>The work order ID is invalid or missing.</p>
        </div>
      </div>
    )
  }

  return <TechnicianWorkOrder workOrderId={Number(workOrderId)} />
}

// Component to display current auth state
const DebugAuthState = () => {
  const { user, token, tokenExpires, isAuthenticated, isTokenExpired } = useAppStore()
  
  return (
    <div style={{ textAlign: 'left', maxWidth: '600px', margin: '0 auto', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
      <h3>Current Authentication State:</h3>
      <pre style={{ fontSize: '14px', overflow: 'auto' }}>
        {JSON.stringify({
          user,
          token: token ? `${token.substring(0, 20)}...` : null,
          tokenExpires,
          isAuthenticated: isAuthenticated(),
          isTokenExpired: isTokenExpired(),
          localStorage: localStorage.getItem('app-storage') ? 'has data' : 'empty'
        }, null, 2)}
      </pre>
    </div>
  )
}

export default App
